import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import { SecondSourcePicker } from './target/SecondSourcePicker';
import { TargetCageField } from './target/TargetCageField';
import { TargetCountFields } from './target/TargetCountFields';
import { TargetNameField } from './target/TargetNameField';
import {
    emptyTargetCandidates,
    occupiedTargetCandidates,
    secondSourceCandidates as secondSourceCandidatesIn,
    targetAllocation,
    targetBreakdown,
} from './target/targetCandidates';
import { useMergeTargetPrefill } from './target/useMergeTargetPrefill';

import styles from '../../operateModals.module.scss';

interface Props {
    initialSource: WizardSource;
    locationCages: OverviewCage[];
    /** `destinations[1]` — the merge target row, when picked. */
    mergeTargetDestination: WizardDestination | null;
    /** From state — drives the absorbHint and the disabled state of the picker. */
    samTargetMode: 'occupied' | 'empty' | null;
    sources: WizardSource[];
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Split-and-merge — Step 2 (Target). The operator picks the cage that
 * receives the split portion of the initial fish group. The cage can be:
 *
 *  - **Occupied by another FG**: that FG is auto-registered as the second
 *    source and absorbed wholesale; no further input needed in this step.
 *  - **Empty**: a second-source picker is inlined immediately below the
 *    cage selector so the operator can explicitly choose the FG that gets
 *    absorbed. Centralizing it here (instead of as a separate step) avoids
 *    the back-and-forth that would otherwise be needed when the prefill
 *    doesn't apply (no populations / no continuation count) and the
 *    operator just wants to enter the merge target count manually.
 *
 * Either way the produced operation lands at `destinations[1]` with two
 * inflows (initial + second source), so the backend infers `split_and_merge`
 * (2 sources, 2 destinations, one with multi-inflow).
 */
export default function TargetStep(props: Props) {
    const {
        initialSource,
        locationCages,
        mergeTargetDestination,
        samTargetMode,
        sources,
        destinations,
        populationById,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');

    const occupiedCandidates = occupiedTargetCandidates(locationCages, initialSource.fishGroupId);
    const emptyCandidates = emptyTargetCandidates(locationCages);

    const noCandidates = occupiedCandidates.length === 0 && emptyCandidates.length === 0;

    const selectedCageId = mergeTargetDestination?.cageId ?? null;

    // The second source is whatever ends up at sources[1] — set automatically
    // by setOccupiedTarget (occupied branch) or by the inline picker below
    // (empty branch).
    const secondSourceId =
        sources.find((s) => s.fishGroupId !== initialSource.fishGroupId)?.fishGroupId ?? null;

    const secondSourceCandidates =
        samTargetMode === 'empty'
            ? secondSourceCandidatesIn(locationCages, initialSource.fishGroupId)
            : [];

    const continuationCountRaw = destinations[0]?.estimatedFishCount ?? '';
    const initialPopulation = populationById.get(initialSource.fishGroupId)?.initialPopulationSize;
    const secondSourcePopulation =
        secondSourceId != null
            ? populationById.get(secondSourceId)?.initialPopulationSize
            : undefined;
    useMergeTargetPrefill({
        dispatch,
        mergeTargetDestination,
        secondSourceId,
        continuationCountRaw,
        initialPopulation,
        secondSourcePopulation,
    });

    const { summary: allocationSummary, remainingFromInitial } = targetAllocation({
        continuationCountRaw,
        initialPopulation,
        secondSourceId,
        secondSourcePopulation,
        mergeTargetCountRaw: mergeTargetDestination?.estimatedFishCount ?? '',
    });

    const secondSourceName =
        secondSourceId != null
            ? (sources.find((s) => s.fishGroupId === secondSourceId)?.name ?? '')
            : '';
    const allocationBreakdown = targetBreakdown({
        samTargetMode,
        initialSourceId: initialSource.fishGroupId,
        splitPortionLabel: t('actions.operate.splitAndMerge.target.allocation.splitPortionLabel', {
            sourceName: initialSource.name,
        }),
        remainingFromInitial,
        secondSourceId,
        secondSourceName,
        secondSourcePopulation,
    });

    // The Count / Notes are gated on the merge target being fully defined:
    //  - cage chosen, AND
    //  - on the empty branch, the second source also chosen.
    // The occupied branch defines both at once when the cage is picked.
    const targetFullyDefined =
        mergeTargetDestination != null &&
        mergeTargetDestination.cageId != null &&
        (samTargetMode === 'occupied' || secondSourceId != null);

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>
                    {t('actions.operate.splitAndMerge.target.heading')}
                </h4>
                <p className={styles.description}>
                    {t('actions.operate.splitAndMerge.target.description')}
                </p>
            </div>

            <Card radius="sm" variant="subtle" padding="none" className={styles.contextCard}>
                <Overline>{t('actions.operate.splitAndMerge.sourceLabel')}</Overline>
                <span className={styles.contextValue}>{initialSource.name}</span>
            </Card>

            <AllocationSummary
                summary={allocationSummary}
                // `sources` is unused in this step — the breakdown is fully
                // driven by `breakdownOverride` so the items can carry the
                // derived "remaining from initial" figure alongside the
                // second source's full population.
                sources={[]}
                populationById={populationById}
                titleKey="actions.operate.splitAndMerge.target.allocationTitle"
                breakdownOverride={allocationBreakdown}
            />

            {noCandidates ? (
                <EmptyState
                    title={t('actions.operate.splitAndMerge.target.empty')}
                    appearance="quiet"
                />
            ) : (
                <Card as="article" radius="sm" padding="xs" className={styles.destinationCard}>
                    <div className={styles.fieldsGrid}>
                        {mergeTargetDestination && (
                            <TargetNameField
                                mergeTargetDestination={mergeTargetDestination}
                                locationCages={locationCages}
                                dispatch={dispatch}
                            />
                        )}

                        <TargetCageField
                            initialSourceId={initialSource.fishGroupId}
                            mergeTargetDestination={mergeTargetDestination}
                            samTargetMode={samTargetMode}
                            selectedCageId={selectedCageId}
                            emptyCandidates={emptyCandidates}
                            occupiedCandidates={occupiedCandidates}
                            dispatch={dispatch}
                        />

                        {samTargetMode === 'empty' && (
                            <SecondSourcePicker
                                secondSourceId={secondSourceId}
                                secondSourceCandidates={secondSourceCandidates}
                                dispatch={dispatch}
                            />
                        )}

                        {targetFullyDefined && mergeTargetDestination && (
                            <TargetCountFields
                                mergeTargetDestination={mergeTargetDestination}
                                dispatch={dispatch}
                            />
                        )}
                    </div>
                </Card>
            )}
        </section>
    );
}
